/** @module @liquidefy/code-editor/diff/diff-compute @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  DiffAlgorithm,
  DiffResult,
  DiffHunk,
  DiffLine,
  DiffStats,
  DiffId,
  HunkId,
  CharRange,
  MovedBlock,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asDiffId,
  asHunkId,
  now,
  emptyDiffResult,
  DEFAULT_DIFF_CONFIG,
} from '../code-editor.js';

// ═══ §2 DIFF COMPUTATION — computeDiff, computeLineDiff, computeCharDiff, computeWordDiff ═══

export function computeDiff(
  original: string,
  modified: string,
  algorithm: DiffAlgorithm = DEFAULT_DIFF_CONFIG.algorithm
): EditorResult<DiffResult> {
  const originalLines = splitLines(original);
  const modifiedLines = splitLines(modified);
  const hunks = computeLineDiff(originalLines, modifiedLines, algorithm);
  const stats = computeStats(hunks);
  const id = asDiffId(generateId('diff-'));
  const result: DiffResult = {
    id,
    algorithm,
    original,
    modified,
    hunks,
    stats,
  };
  return ok(result, 'DIFF_COMPUTED', `Diff computed with ${algorithm} algorithm: ${stats.additions} additions, ${stats.deletions} deletions`);
}

export function computeLineDiff(
  originalLines: string[],
  modifiedLines: string[],
  algorithm: DiffAlgorithm = 'myers'
): DiffHunk[] {
  switch (algorithm) {
    case 'patience': return patienceDiff(originalLines, modifiedLines);
    case 'histogram': return histogramDiff(originalLines, modifiedLines);
    case 'myers':
    default: return myersDiff(originalLines, modifiedLines);
  }
}

export function computeCharDiff(oldLine: string, newLine: string): CharRange[] {
  const ranges: CharRange[] = [];
  const oldChars = oldLine.split('');
  const newChars = newLine.split('');
  const lcs = lcsChars(oldChars, newChars);
  let oi = 0;
  let ni = 0;
  let li = 0;
  let rangeStart = -1;

  while (ni < newChars.length) {
    if (li < lcs.length && oi < oldChars.length && oldChars[oi] === lcs[li] && newChars[ni] === lcs[li]) {
      if (rangeStart !== -1) {
        ranges.push({ startColumn: rangeStart, endColumn: ni });
        rangeStart = -1;
      }
      oi++;
      ni++;
      li++;
    } else if (li < lcs.length && newChars[ni] === lcs[li]) {
      if (rangeStart !== -1) {
        ranges.push({ startColumn: rangeStart, endColumn: ni });
        rangeStart = -1;
      }
      ni++;
      li++;
    } else {
      if (rangeStart === -1) rangeStart = ni;
      ni++;
    }
  }
  if (rangeStart !== -1) {
    ranges.push({ startColumn: rangeStart, endColumn: ni });
  }
  return ranges;
}

export function computeWordDiff(oldLine: string, newLine: string): CharRange[] {
  const oldWords = tokenizeWords(oldLine);
  const newWords = tokenizeWords(newLine);
  const ranges: CharRange[] = [];

  const lcs = lcsStrings(oldWords.map(w => w.text), newWords.map(w => w.text));
  let li = 0;
  let changeStart = -1;

  for (let ni = 0; ni < newWords.length; ni++) {
    if (li < lcs.length && newWords[ni]!.text === lcs[li]) {
      if (changeStart !== -1) {
        ranges.push({ startColumn: changeStart, endColumn: newWords[ni]!.start });
        changeStart = -1;
      }
      li++;
    } else {
      if (changeStart === -1) changeStart = newWords[ni]!.start;
    }
  }
  if (changeStart !== -1) {
    ranges.push({ startColumn: changeStart, endColumn: newLine.length });
  }
  return ranges;
}

function splitLines(text: string): string[] {
  if (text.length === 0) return [];
  return text.split(/\r\n|\r|\n/);
}

function tokenizeWords(line: string): Array<{ text: string; start: number; end: number }> {
  const words: Array<{ text: string; start: number; end: number }> = [];
  const regex = /\S+|\s+/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(line)) !== null) {
    words.push({ text: match[0], start: match.index, end: match.index + match[0].length });
  }
  return words;
}

function lcsChars(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i]![j] = dp[i - 1]![j - 1]! + 1;
      else dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
    }
  }
  const result: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]!);
      i--;
      j--;
    } else if (dp[i - 1]![j]! > dp[i]![j - 1]!) {
      i--;
    } else {
      j--;
    }
  }
  return result;
}

function lcsStrings(a: string[], b: string[]): string[] {
  return lcsChars(a, b);
}

// ═══ §3 MYERS ALGORITHM — myersDiff, shortestEditScript, traceToHunks ═══

export const MYERS_MAX_DIFF = 10000;

export function myersDiff(original: string[], modified: string[]): DiffHunk[] {
  const n = original.length;
  const m = modified.length;

  if (n === 0 && m === 0) return [];
  if (n === 0) return [createAddHunk(modified, 0)];
  if (m === 0) return [createDeleteHunk(original, 0)];

  const trace = shortestEditScript(original, modified);
  return traceToHunks(trace, original, modified);
}

export function shortestEditScript(a: string[], b: string[]): Array<Map<number, number>> {
  const n = a.length;
  const m = b.length;
  const max = Math.min(n + m, MYERS_MAX_DIFF);
  const v = new Map<number, number>();
  v.set(1, 0);
  const trace: Array<Map<number, number>> = [];

  for (let d = 0; d <= max; d++) {
    trace.push(new Map(v));
    for (let k = -d; k <= d; k += 2) {
      let x: number;
      if (k === -d || (k !== d && (v.get(k - 1) ?? 0) < (v.get(k + 1) ?? 0))) {
        x = v.get(k + 1) ?? 0;
      } else {
        x = (v.get(k - 1) ?? 0) + 1;
      }
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) {
        x++;
        y++;
      }
      v.set(k, x);
      if (x >= n && y >= m) return trace;
    }
  }
  return trace;
}

export function traceToHunks(
  trace: Array<Map<number, number>>,
  a: string[],
  b: string[]
): DiffHunk[] {
  let x = a.length;
  let y = b.length;
  const edits: Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }> = [];

  for (let d = trace.length - 1; d >= 0; d--) {
    const v = trace[d];
    const k = x - y;
    let prevK: number;

    if (k === -d || (k !== d && (v!.get(k - 1) ?? 0) < (v!.get(k + 1) ?? 0))) {
      prevK = k + 1;
    } else {
      prevK = k - 1;
    }

    const prevX = v!.get(prevK) ?? 0;
    const prevY = prevX - prevK;

    while (x > prevX && y > prevY) {
      x--;
      y--;
      edits.unshift({ type: 'equal', oldIdx: x, newIdx: y, content: a[x]! });
    }

    if (d > 0) {
      if (x === prevX) {
        y--;
        edits.unshift({ type: 'add', oldIdx: x, newIdx: y, content: b[y]! });
      } else {
        x--;
        edits.unshift({ type: 'delete', oldIdx: x, newIdx: y, content: a[x]! });
      }
    }
  }

  return editsToHunks(edits, a, b);
}

function editsToHunks(
  edits: Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }>,
  _a: string[],
  _b: string[]
): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let currentLines: DiffLine[] = [];
  let hunkOldStart = -1;
  let hunkNewStart = -1;
  let hunkOldCount = 0;
  let hunkNewCount = 0;
  let lastChangeIdx = -1;
  const contextLines = DEFAULT_DIFF_CONFIG.contextLines;

  for (let i = 0; i < edits.length; i++) {
    const edit = edits[i];
    const isChange = edit!.type !== 'equal';

    if (isChange) {
      if (hunkOldStart === -1) {
        const contextStart = Math.max(0, i - contextLines);
        hunkOldStart = edits[contextStart]!.oldIdx;
        hunkNewStart = edits[contextStart]!.newIdx;
        for (let c = contextStart; c < i; c++) {
          const ce = edits[c];
          currentLines.push({
            type: 'equal',
            content: ce!.content,
            oldLineNumber: ce!.oldIdx + 1,
            newLineNumber: ce!.newIdx + 1,
          });
          hunkOldCount++;
          hunkNewCount++;
        }
      } else if (lastChangeIdx !== -1 && i - lastChangeIdx > contextLines * 2) {
        for (let c = lastChangeIdx + 1; c <= lastChangeIdx + contextLines && c < edits.length; c++) {
          const ce = edits[c];
          if (ce!.type === 'equal') {
            currentLines.push({
              type: 'equal',
              content: ce!.content,
              oldLineNumber: ce!.oldIdx + 1,
              newLineNumber: ce!.newIdx + 1,
            });
            hunkOldCount++;
            hunkNewCount++;
          }
        }
        hunks.push(finalizeHunk(hunkOldStart, hunkOldCount, hunkNewStart, hunkNewCount, currentLines));
        currentLines = [];
        hunkOldCount = 0;
        hunkNewCount = 0;
        const contextStart = Math.max(0, i - contextLines);
        hunkOldStart = edits[contextStart]!.oldIdx;
        hunkNewStart = edits[contextStart]!.newIdx;
        for (let c = contextStart; c < i; c++) {
          const ce = edits[c];
          if (ce!.type === 'equal') {
            currentLines.push({
              type: 'equal',
              content: ce!.content,
              oldLineNumber: ce!.oldIdx + 1,
              newLineNumber: ce!.newIdx + 1,
            });
            hunkOldCount++;
            hunkNewCount++;
          }
        }
      } else {
        for (let c = lastChangeIdx + 1; c < i; c++) {
          const ce = edits[c];
          if (ce!.type === 'equal') {
            currentLines.push({
              type: 'equal',
              content: ce!.content,
              oldLineNumber: ce!.oldIdx + 1,
              newLineNumber: ce!.newIdx + 1,
            });
            hunkOldCount++;
            hunkNewCount++;
          }
        }
      }

      if (edit!.type === 'delete') {
        currentLines.push({
          type: 'delete',
          content: edit!.content,
          oldLineNumber: edit!.oldIdx + 1,
        });
        hunkOldCount++;
      } else {
        currentLines.push({
          type: 'add',
          content: edit!.content,
          newLineNumber: edit!.newIdx + 1,
        });
        hunkNewCount++;
      }
      lastChangeIdx = i;
    }
  }

  if (currentLines.length > 0) {
    if (lastChangeIdx !== -1) {
      for (let c = lastChangeIdx + 1; c < Math.min(edits.length, lastChangeIdx + 1 + contextLines); c++) {
        const ce = edits[c];
        if (ce!.type === 'equal') {
          currentLines.push({
            type: 'equal',
            content: ce!.content,
            oldLineNumber: ce!.oldIdx + 1,
            newLineNumber: ce!.newIdx + 1,
          });
          hunkOldCount++;
          hunkNewCount++;
        }
      }
    }
    hunks.push(finalizeHunk(hunkOldStart, hunkOldCount, hunkNewStart, hunkNewCount, currentLines));
  }

  return hunks;
}

function createAddHunk(lines: string[], startLine: number): DiffHunk {
  return {
    id: asHunkId(generateId('hunk-')),
    oldStart: startLine + 1,
    oldCount: 0,
    newStart: startLine + 1,
    newCount: lines.length,
    lines: lines.map((l, i) => ({
      type: 'add' as const,
      content: l,
      newLineNumber: startLine + i + 1,
    })),
    isConflict: false,
  };
}

function createDeleteHunk(lines: string[], startLine: number): DiffHunk {
  return {
    id: asHunkId(generateId('hunk-')),
    oldStart: startLine + 1,
    oldCount: lines.length,
    newStart: startLine + 1,
    newCount: 0,
    lines: lines.map((l, i) => ({
      type: 'delete' as const,
      content: l,
      oldLineNumber: startLine + i + 1,
    })),
    isConflict: false,
  };
}

function finalizeHunk(
  oldStart: number,
  oldCount: number,
  newStart: number,
  newCount: number,
  lines: DiffLine[]
): DiffHunk {
  return {
    id: asHunkId(generateId('hunk-')),
    oldStart: oldStart + 1,
    oldCount,
    newStart: newStart + 1,
    newCount,
    lines,
    isConflict: false,
  };
}

// ═══ §4 PATIENCE ALGORITHM — patienceDiff, longestCommonSubsequence, uniqueLines, patienceSort ═══

export function patienceDiff(original: string[], modified: string[]): DiffHunk[] {
  if (original.length === 0 && modified.length === 0) return [];
  if (original.length === 0) return [createAddHunk(modified, 0)];
  if (modified.length === 0) return [createDeleteHunk(original, 0)];

  const uniqueOrig = uniqueLines(original);
  const uniqueMod = uniqueLines(modified);

  const commonUnique: Array<[number, number]> = [];
  for (const [line, origIdx] of uniqueOrig) {
    const modIdx = uniqueMod.get(line);
    if (modIdx !== undefined) {
      commonUnique.push([origIdx, modIdx]);
    }
  }

  if (commonUnique.length === 0) {
    return myersDiff(original, modified);
  }

  const lcsAnchors = patienceSort(commonUnique);
  return buildPatienceHunks(original, modified, lcsAnchors);
}

export function uniqueLines(lines: string[]): Map<string, number> {
  const counts = new Map<string, number>();
  const positions = new Map<string, number>();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    counts.set(line!, (counts.get(line!) ?? 0) + 1);
    positions.set(line!, i);
  }

  const result = new Map<string, number>();
  for (const [line, count] of counts) {
    if (count === 1) {
      result.set(line, positions.get(line)!);
    }
  }
  return result;
}

export function longestCommonSubsequence(a: string[], b: string[]): string[] {
  return lcsStrings(a, b);
}

export function patienceSort(pairs: Array<[number, number]>): Array<[number, number]> {
  const sorted = [...pairs].sort((x, y) => x[0] - y[0]);
  const piles: Array<Array<{ pair: [number, number]; prev?: number }>> = [];

  for (const pair of sorted) {
    const bVal = pair[1];
    let lo = 0;
    let hi = piles.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const top = piles[mid]![piles[mid]!.length - 1];
      if (top!.pair[1] < bVal) lo = mid + 1;
      else hi = mid;
    }
    const entry = { pair, prev: lo > 0 ? piles[lo - 1]!.length - 1 : undefined };
    if (lo === piles.length) piles.push([entry]);
    else piles[lo]!.push(entry);
  }

  if (piles.length === 0) return [];

  const result: Array<[number, number]> = [];
  let pileIdx = piles.length - 1;
  let entryIdx = piles[pileIdx]!.length - 1;
  while (pileIdx >= 0) {
    const entry = piles[pileIdx]![entryIdx];
    result.unshift(entry!.pair);
    entryIdx = entry!.prev ?? 0;
    pileIdx--;
  }
  return result;
}

function buildPatienceHunks(
  original: string[],
  modified: string[],
  anchors: Array<[number, number]>
): DiffHunk[] {
  const allEdits: Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }> = [];
  let oi = 0;
  let mi = 0;

  for (const [anchorO, anchorM] of anchors) {
    while (oi < anchorO && mi < anchorM) {
      if (original[oi] === modified[mi]) {
        allEdits.push({ type: 'equal', oldIdx: oi, newIdx: mi, content: original[oi]! });
        oi++;
        mi++;
      } else {
        allEdits.push({ type: 'delete', oldIdx: oi, newIdx: mi, content: original[oi]! });
        oi++;
      }
    }
    while (oi < anchorO) {
      allEdits.push({ type: 'delete', oldIdx: oi, newIdx: mi, content: original[oi]! });
      oi++;
    }
    while (mi < anchorM) {
      allEdits.push({ type: 'add', oldIdx: oi, newIdx: mi, content: modified[mi]! });
      mi++;
    }
    allEdits.push({ type: 'equal', oldIdx: oi, newIdx: mi, content: original[oi]! });
    oi++;
    mi++;
  }

  while (oi < original.length) {
    allEdits.push({ type: 'delete', oldIdx: oi, newIdx: mi, content: original[oi]! });
    oi++;
  }
  while (mi < modified.length) {
    allEdits.push({ type: 'add', oldIdx: oi, newIdx: mi, content: modified[mi]! });
    mi++;
  }

  return editsToHunks(allEdits, original, modified);
}

// ═══ §5 HISTOGRAM ALGORITHM — histogramDiff, buildHistogram, findLowOccurrenceLCS, recursiveDiff ═══

export function histogramDiff(original: string[], modified: string[]): DiffHunk[] {
  if (original.length === 0 && modified.length === 0) return [];
  if (original.length === 0) return [createAddHunk(modified, 0)];
  if (modified.length === 0) return [createDeleteHunk(original, 0)];

  const edits = recursiveDiff(original, modified, 0, original.length, 0, modified.length);
  return editsToHunks(edits, original, modified);
}

export function buildHistogram(lines: string[]): Map<string, number> {
  const histogram = new Map<string, number>();
  for (const line of lines) {
    histogram.set(line, (histogram.get(line) ?? 0) + 1);
  }
  return histogram;
}

export function findLowOccurrenceLCS(
  a: string[],
  b: string[],
  histogram: Map<string, number>
): Array<[number, number]> {
  const maxOccurrence = Math.max(1, Math.floor(Math.sqrt(a.length)));
  const lowOccA: Array<[string, number]> = [];

  for (let i = 0; i < a.length; i++) {
    const count = histogram.get(a[i]!) ?? 0;
    if (count <= maxOccurrence) {
      lowOccA.push([a[i]!, i]);
    }
  }

  const bPositions = new Map<string, number[]>();
  for (let j = 0; j < b.length; j++) {
    const count = histogram.get(b[j]!) ?? 0;
    if (count <= maxOccurrence) {
      if (!bPositions.has(b[j]!)) bPositions.set(b[j]!, []);
      bPositions.get(b[j]!)!.push(j);
    }
  }

  const pairs: Array<[number, number]> = [];
  for (const [line, aIdx] of lowOccA) {
    const bIdxs = bPositions.get(line);
    if (bIdxs && bIdxs.length === 1) {
      pairs.push([aIdx, bIdxs[0]!]);
    }
  }

  return patienceSort(pairs);
}

export function recursiveDiff(
  a: string[],
  b: string[],
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number
): Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }> {
  const aSlice = a.slice(aStart, aEnd);
  const bSlice = b.slice(bStart, bEnd);

  if (aSlice.length === 0 && bSlice.length === 0) return [];

  if (aSlice.length === 0) {
    return bSlice.map((line, i) => ({
      type: 'add' as const,
      oldIdx: aStart,
      newIdx: bStart + i,
      content: line,
    }));
  }

  if (bSlice.length === 0) {
    return aSlice.map((line, i) => ({
      type: 'delete' as const,
      oldIdx: aStart + i,
      newIdx: bStart,
      content: line,
    }));
  }

  const combined = [...aSlice, ...bSlice];
  const histogram = buildHistogram(combined);
  const anchors = findLowOccurrenceLCS(aSlice, bSlice, histogram);

  if (anchors.length === 0) {
    const edits: Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }> = [];
    for (let i = 0; i < aSlice.length; i++) {
      edits.push({ type: 'delete', oldIdx: aStart + i, newIdx: bStart, content: aSlice[i]! });
    }
    for (let i = 0; i < bSlice.length; i++) {
      edits.push({ type: 'add', oldIdx: aEnd, newIdx: bStart + i, content: bSlice[i]! });
    }
    return edits;
  }

  const edits: Array<{ type: 'delete' | 'add' | 'equal'; oldIdx: number; newIdx: number; content: string }> = [];
  let ai = 0;
  let bi = 0;

  for (const [anchorA, anchorB] of anchors) {
    const subEdits = recursiveDiff(a, b, aStart + ai, aStart + anchorA, bStart + bi, bStart + anchorB);
    edits.push(...subEdits);
    edits.push({
      type: 'equal',
      oldIdx: aStart + anchorA,
      newIdx: bStart + anchorB,
      content: aSlice[anchorA]!,
    });
    ai = anchorA + 1;
    bi = anchorB + 1;
  }

  const tailEdits = recursiveDiff(a, b, aStart + ai, aEnd, bStart + bi, bEnd);
  edits.push(...tailEdits);
  return edits;
}

// ═══ §6 HUNK MANAGEMENT — normalizeHunks, expandContext, splitHunk, mergeHunks, isHunkEmpty, hunkStats ═══

export function normalizeHunks(hunks: DiffHunk[]): DiffHunk[] {
  if (hunks.length <= 1) return hunks;
  const sorted = [...hunks].sort((a, b) => a.oldStart - b.oldStart);
  const result: DiffHunk[] = [sorted[0]!];

  for (let i = 1; i < sorted.length; i++) {
    const prev = result[result.length - 1];
    const curr = sorted[i]!;
    if (hunksOverlap(prev!, curr) || curr!.oldStart <= prev!.oldStart + prev!.oldCount) {
      result[result.length - 1] = mergeHunks(prev!, curr);
    } else {
      result.push(curr!);
    }
  }
  return result;
}

export function expandContext(
  hunks: DiffHunk[],
  original: string[],
  modified: string[],
  contextLines: number
): DiffHunk[] {
  return hunks.map(hunk => {
    const newLines: DiffLine[] = [];
    const contextBefore = Math.max(0, hunk.oldStart - 1 - contextLines);
    for (let i = contextBefore; i < hunk.oldStart - 1; i++) {
      if (i < original.length) {
        newLines.push({
          type: 'equal',
          content: original[i]!,
          oldLineNumber: i + 1,
          newLineNumber: hunk.newStart - (hunk.oldStart - 1 - i),
        });
      }
    }
    newLines.push(...hunk.lines);
    const contextAfterStart = hunk.oldStart - 1 + hunk.oldCount;
    for (let i = contextAfterStart; i < Math.min(original.length, contextAfterStart + contextLines); i++) {
      newLines.push({
        type: 'equal',
        content: original[i]!,
        oldLineNumber: i + 1,
        newLineNumber: hunk.newStart + hunk.newCount + (i - contextAfterStart),
      });
    }
    const addedBefore = hunk.oldStart - 1 - contextBefore;
    const addedAfter = Math.min(original.length, contextAfterStart + contextLines) - contextAfterStart;
    return {
      ...hunk,
      oldStart: contextBefore + 1,
      oldCount: hunk.oldCount + addedBefore + addedAfter,
      newCount: hunk.newCount + addedBefore + addedAfter,
      lines: newLines,
    };
  });
}

export function splitHunk(hunk: DiffHunk, line: number): [DiffHunk, DiffHunk] {
  const idx = hunk.lines.findIndex(l =>
    (l.oldLineNumber === line || l.newLineNumber === line)
  );
  if (idx <= 0 || idx >= hunk.lines.length) {
    return [hunk, { ...hunk, lines: [], oldCount: 0, newCount: 0 }];
  }
  const firstLines = hunk.lines.slice(0, idx);
  const secondLines = hunk.lines.slice(idx);
  const firstStats = linesStats(firstLines);
  const secondStats = linesStats(secondLines);

  const first: DiffHunk = {
    id: asHunkId(generateId('hunk-')),
    oldStart: hunk.oldStart,
    oldCount: firstStats.oldCount,
    newStart: hunk.newStart,
    newCount: firstStats.newCount,
    lines: firstLines,
    isConflict: false,
  };
  const second: DiffHunk = {
    id: asHunkId(generateId('hunk-')),
    oldStart: hunk.oldStart + firstStats.oldCount,
    oldCount: secondStats.oldCount,
    newStart: hunk.newStart + firstStats.newCount,
    newCount: secondStats.newCount,
    lines: secondLines,
    isConflict: false,
  };
  return [first, second];
}

export function mergeHunks(a: DiffHunk, b: DiffHunk): DiffHunk {
  const first = a.oldStart <= b.oldStart ? a : b;
  const second = a.oldStart <= b.oldStart ? b : a;
  return {
    id: asHunkId(generateId('hunk-')),
    oldStart: first.oldStart,
    oldCount: Math.max(first.oldStart + first.oldCount, second.oldStart + second.oldCount) - first.oldStart,
    newStart: first.newStart,
    newCount: Math.max(first.newStart + first.newCount, second.newStart + second.newCount) - first.newStart,
    lines: [...first.lines, ...second.lines],
    isConflict: first.isConflict || second.isConflict,
  };
}

export function isHunkEmpty(hunk: DiffHunk): boolean {
  return hunk.lines.length === 0 || hunk.lines.every(l => l.type === 'equal');
}

export function hunkStats(hunk: DiffHunk): { additions: number; deletions: number } {
  let additions = 0;
  let deletions = 0;
  for (const line of hunk.lines) {
    if (line.type === 'add') additions++;
    else if (line.type === 'delete') deletions++;
  }
  return { additions, deletions };
}

export function hunksOverlap(a: DiffHunk, b: DiffHunk): boolean {
  const aEnd = a.oldStart + a.oldCount;
  const bEnd = b.oldStart + b.oldCount;
  return a.oldStart < bEnd && b.oldStart < aEnd;
}

function linesStats(lines: DiffLine[]): { oldCount: number; newCount: number } {
  let oldCount = 0;
  let newCount = 0;
  for (const line of lines) {
    if (line.type === 'delete' || line.type === 'equal') oldCount++;
    if (line.type === 'add' || line.type === 'equal') newCount++;
  }
  return { oldCount, newCount };
}

// ═══ §7 MOVED BLOCK DETECTION — detectMovedBlocks, blockSimilarity, hashLines, isMovedNotChanged ═══

export const MOVE_THRESHOLD = 0.8;

export function detectMovedBlocks(hunks: DiffHunk[], threshold: number = MOVE_THRESHOLD): MovedBlock[] {
  const deletions: Array<{ lines: string[]; hunkIdx: number; startLine: number }> = [];
  const additions: Array<{ lines: string[]; hunkIdx: number; startLine: number }> = [];

  for (let hi = 0; hi < hunks.length; hi++) {
    const hunk = hunks[hi];
    let delBlock: string[] = [];
    let delStart = 0;
    let addBlock: string[] = [];
    let addStart = 0;

    for (const line of hunk!.lines) {
      if (line.type === 'delete') {
        if (delBlock.length === 0) delStart = line.oldLineNumber ?? 0;
        delBlock.push(line.content);
      } else {
        if (delBlock.length > 0) {
          deletions.push({ lines: delBlock, hunkIdx: hi, startLine: delStart });
          delBlock = [];
        }
      }
      if (line.type === 'add') {
        if (addBlock.length === 0) addStart = line.newLineNumber ?? 0;
        addBlock.push(line.content);
      } else {
        if (addBlock.length > 0) {
          additions.push({ lines: addBlock, hunkIdx: hi, startLine: addStart });
          addBlock = [];
        }
      }
    }
    if (delBlock.length > 0) deletions.push({ lines: delBlock, hunkIdx: hi, startLine: delStart });
    if (addBlock.length > 0) additions.push({ lines: addBlock, hunkIdx: hi, startLine: addStart });
  }

  const moved: MovedBlock[] = [];
  const usedDeletions = new Set<number>();
  const usedAdditions = new Set<number>();

  for (let di = 0; di < deletions.length; di++) {
    if (usedDeletions.has(di)) continue;
    for (let ai = 0; ai < additions.length; ai++) {
      if (usedAdditions.has(ai)) continue;
      const sim = blockSimilarity(deletions[di]!.lines, additions[ai]!.lines);
      if (sim >= threshold) {
        const delLen = deletions[di]!.lines.length;
        const addLen = additions[ai]!.lines.length;
        moved.push({
          originalRange: {
            start: { line: deletions[di]!.startLine, column: 0 },
            end: { line: deletions[di]!.startLine + delLen, column: 0 },
          },
          modifiedRange: {
            start: { line: additions[ai]!.startLine, column: 0 },
            end: { line: additions[ai]!.startLine + addLen, column: 0 },
          },
          similarity: sim,
        });
        usedDeletions.add(di);
        usedAdditions.add(ai);
        break;
      }
    }
  }
  return moved;
}

export function blockSimilarity(a: string[], b: string[]): number {
  if (a.length === 0 && b.length === 0) return 1;
  if (a.length === 0 || b.length === 0) return 0;
  const hashA = hashLines(a);
  const hashB = hashLines(b);
  if (hashA === hashB) return 1;

  const lcs = lcsStrings(a, b);
  const maxLen = Math.max(a.length, b.length);
  return lcs.length / maxLen;
}

export function hashLines(lines: string[]): number {
  let hash = 0;
  for (const line of lines) {
    for (let i = 0; i < line.length; i++) {
      hash = ((hash << 5) - hash + line.charCodeAt(i)) | 0;
    }
    hash = ((hash << 5) - hash + 10) | 0;
  }
  return hash;
}

export function isMovedNotChanged(deletion: string[], addition: string[]): boolean {
  if (deletion.length !== addition.length) return false;
  for (let i = 0; i < deletion.length; i++) {
    if (deletion[i] !== addition[i]) return false;
  }
  return true;
}

// ═══ §8 DIFF STATS — computeStats, aggregateStats, diffComplexity, isLargeDiff, changePercentage ═══

export function computeStats(hunks: DiffHunk[]): DiffStats {
  let additions = 0;
  let deletions = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add') additions++;
      else if (line.type === 'delete') deletions++;
    }
  }
  return {
    additions,
    deletions,
    changes: additions + deletions,
  };
}

export function aggregateStats(results: DiffResult[]): DiffStats {
  let additions = 0;
  let deletions = 0;
  for (const result of results) {
    additions += result.stats.additions;
    deletions += result.stats.deletions;
  }
  return {
    additions,
    deletions,
    changes: additions + deletions,
    fileCount: results.length,
  };
}

export function diffComplexity(result: DiffResult): number {
  const hunkCount = result.hunks.length;
  const totalChanges = result.stats.changes;
  const scatterFactor = hunkCount > 0 ? totalChanges / hunkCount : 0;
  return Math.round(totalChanges * (1 + Math.log2(Math.max(hunkCount, 1))) + scatterFactor);
}

export function isLargeDiff(stats: DiffStats, threshold: number = 500): boolean {
  return stats.changes > threshold;
}

export function isSignificantChange(stats: DiffStats): boolean {
  return stats.changes > 10 || (stats.additions > 5 && stats.deletions > 5);
}

export function changePercentage(stats: DiffStats, totalLines: number): number {
  if (totalLines <= 0) return 0;
  return Math.min(100, (stats.changes / totalLines) * 100);
}
